import { useEffect, useState } from "react";
import { onPrefs, pushPref } from "@/lib/prefsSync";

export const VIZ_MODES = [
  { id: "led", label: "Neon LED" },
  { id: "tint", label: "Album tint" },
  { id: "rainbow", label: "Rainbow" },
  { id: "flow", label: "Rainbow flow" },
];
const KEY = "viz_mode2";
const EVT = "viz-mode";

export const loadVizMode = () => {
  const v = localStorage.getItem(KEY);
  return VIZ_MODES.some((m) => m.id === v) ? v : "led";
};
const syncViz = () => pushPref("viz", { mode: loadVizMode(), led: loadLedStyle() });
export const saveVizMode = (mode, push = true) => { localStorage.setItem(KEY, mode); window.dispatchEvent(new CustomEvent(EVT, { detail: mode })); if (push) syncViz(); };
export const nextVizMode = (mode) => VIZ_MODES[(VIZ_MODES.findIndex((m) => m.id === mode) + 1) % VIZ_MODES.length].id;
export const isRainbow = (mode) => mode !== "tint";

/* Hue (deg) for bar i of n; "flow" drifts the whole sweep over time; "led" sweeps blue → violet → pink → orange → yellow → green like a classic LED analyser. */
export const barHue = (i, n, mode, now) => {
  const t = i / Math.max(1, n - 1);
  if (mode === "led") return (205 + t * 275) % 360;
  return (t * 300 + (mode === "flow" ? now / 25 : 0)) % 360;
};

export function useVizMode() {
  const [mode, set] = useState(loadVizMode);
  useEffect(() => {
    const on = (e) => set(e.detail);
    window.addEventListener(EVT, on);
    return () => window.removeEventListener(EVT, on);
  }, []);
  return [mode, saveVizMode];
}

/* Colour sweep for the LED analyser. */
export const LED_STYLES = [
  { id: "rainbow", label: "Rainbow sweep" },
  { id: "classic", label: "Classic green → red" },
  { id: "tint", label: "Album tint" },
];
const LED_KEY = "viz_led_style";
const LED_EVT = "viz-led-style";
export const loadLedStyle = () => { const v = localStorage.getItem(LED_KEY); return LED_STYLES.some((s) => s.id === v) ? v : "rainbow"; };
export const saveLedStyle = (id, push = true) => { localStorage.setItem(LED_KEY, id); window.dispatchEvent(new CustomEvent(LED_EVT, { detail: id })); if (push) syncViz(); };

// Cross-device sync: one "viz" setting carrying both the mode and the LED colour style.
onPrefs("viz", {
  apply: (v) => {
    if (v?.mode && VIZ_MODES.some((m) => m.id === v.mode)) saveVizMode(v.mode, false);
    if (v?.led && LED_STYLES.some((s) => s.id === v.led)) saveLedStyle(v.led, false);
  },
  read: () => ({ mode: loadVizMode(), led: loadLedStyle() }),
});
export const nextLedStyle = (id) => LED_STYLES[(LED_STYLES.findIndex((s) => s.id === id) + 1) % LED_STYLES.length].id;
export function useLedStyle() {
  const [style, set] = useState(loadLedStyle);
  useEffect(() => {
    const on = (e) => set(e.detail);
    window.addEventListener(LED_EVT, on);
    return () => window.removeEventListener(LED_EVT, on);
  }, []);
  return [style, saveLedStyle];
}

/* Hue of an rgb() / rgba() / hex colour string, for the album-tint LED style. */
export const hueOf = (css) => {
  const m = /(\d+)[,\s]+(\d+)[,\s]+(\d+)/.exec(css || "");
  let r, g, b;
  if (m) [r, g, b] = [m[1], m[2], m[3]].map(Number);
  else if (/^#[0-9a-f]{6}$/i.test(css || "")) [r, g, b] = [1, 3, 5].map((i) => parseInt(css.slice(i, i + 2), 16));
  else return 210;
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  if (!d) return 210;
  const h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return (h * 60 + 360) % 360;
};
